import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import RoomForm, { type RoomRecord } from '@/pages/dashboard/rooms/components/RoomForm';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

function RoomEdit() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [existing, setExisting] = useState<RoomRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!user || !id) return;
      setLoading(true);
      try {
        const { data: biz } = await supabase
          .from('businesses')
          .select('id')
          .eq('owner_id', user.id)
          .maybeSingle();
        if (!mounted) return;
        setBusinessId(biz?.id ?? null);

        const { data: room } = await supabase
          .from('hotel_rooms')
          .select('*')
          .eq('id', id)
          .eq('owner_id', user.id)
          .maybeSingle();

        if (!mounted) return;
        if (!room) {
          setNotFound(true);
        } else {
          const r = room as RoomRecord;
          setExisting({
            id: r.id,
            business_id: r.business_id,
            owner_id: r.owner_id,
            name: r.name || '',
            description: r.description || '',
            price_per_night: Number(r.price_per_night) || 0,
            capacity: Number(r.capacity) || 1,
            bed_type: r.bed_type || 'Lit double',
            images: Array.isArray(r.images) ? r.images : [],
            amenities: Array.isArray(r.amenities) ? r.amenities : [],
            available: Boolean(r.available),
          });
        }
      } catch {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user, id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (notFound || !existing) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">Chambre introuvable</h1>
          <p className="text-sm text-foreground-400 mb-6">Cette chambre n&apos;existe pas ou ne vous appartient pas.</p>
          <button
            onClick={() => navigate('/dashboard/rooms')}
            className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
          >
            Retour à mes chambres
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Modifier la chambre</h1>
        <p className="text-sm text-foreground-400 mt-1">Mettez à jour les informations de votre chambre.</p>
      </div>

      <div className="max-w-3xl">
        <RoomForm
          businessId={businessId}
          ownerId={user!.id}
          existing={existing}
          onSaved={() => navigate('/dashboard/rooms')}
        />
      </div>
    </DashboardLayout>
  );
}

export default RoomEdit;